<template>
  <div class="qst-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader />
      <main class="page-content">

        <!-- ── Checking permission ────────────────────────── -->
        <div v-if="checkingPermission" class="full-state">
          <div class="spinner lg"></div>
          <p class="state-label">Loading…</p>
        </div>

        <!-- ── Access denied ─────────────────────────────── -->
        <div v-else-if="accessDenied" class="full-state">
          <div class="state-icon-wrap denied">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
          </div>
          <h3 class="state-title">Access Restricted</h3>
          <p class="state-desc">You don't have permission to send WhatsApp templates.</p>
        </div>

        <!-- ── Main page ──────────────────────────────────── -->
        <div v-else class="qst-container">

          <!-- Page Header -->
          <div class="page-header">
            <div class="header-left">
              <div class="header-icon-wrap">
                <img src="@/assets/whatsapp-logo-wine2.svg" width="28" height="28" alt="" />
              </div>
              <div>
                <h1 class="page-title">Quick Send Template</h1>
                <p class="page-subtitle">Send a WhatsApp template to one or more contacts instantly</p>
              </div>
            </div>

            <!-- Steps -->
            <div class="steps-bar">
              <div class="step-item" :class="{ done: recipients.length > 0 }">
                <span class="step-num">1</span>
                <span class="step-lbl">Recipients</span>
              </div>
              <svg class="step-arrow" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/></svg>
              <div class="step-item" :class="{ done: !!templateForm.selectedTemplate }">
                <span class="step-num">2</span>
                <span class="step-lbl">Template</span>
              </div>
              <svg class="step-arrow" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/></svg>
              <div class="step-item" :class="{ done: canSend }">
                <span class="step-num">3</span>
                <span class="step-lbl">Send</span>
              </div>
            </div>
          </div>

          <!-- Body -->
          <div class="qst-body">

            <!-- ══ LEFT: Recipients ══ -->
            <div class="qst-panel">
              <div class="panel-hd">
                <div class="panel-hd-left">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" class="panel-hd-icon">
                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                  </svg>
                  <h2 class="panel-title">Recipients</h2>
                </div>
                <span v-if="recipients.length" class="count-chip">{{ recipients.length }}</span>
              </div>

              <!-- Search customers -->
              <div class="field-group" ref="searchWrap">
                <label class="field-label">Search contacts</label>
                <div class="search-box" :class="{ focused: showDropdown }">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" class="search-icon-inner">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                  </svg>
                  <input
                    v-model="customerSearch"
                    type="text"
                    placeholder="Name or phone number…"
                    class="search-input-el"
                    @input="debouncedCustomerSearch"
                    @focus="showDropdown = true"
                    @blur="onSearchBlur"
                    autocomplete="off"
                  />
                  <div v-if="searchLoading" class="spinner xs"></div>
                </div>

                <!-- Dropdown -->
                <div
                  v-if="showDropdown && (searchLoading || searchResults.length > 0 || (customerSearch.length > 1 && !searchLoading))"
                  class="search-dropdown"
                >
                  <div v-if="searchLoading" class="dropdown-msg">
                    <div class="spinner xs"></div> Searching…
                  </div>
                  <template v-else>
                    <div
                      v-for="c in searchResults"
                      :key="c.id"
                      class="dropdown-row"
                      :class="{ 'is-added': isAlreadyAdded(c.phone_number) }"
                      @mousedown.prevent="addRecipientFromCustomer(c)"
                    >
                      <div class="d-avatar">{{ (c.full_name || '?').charAt(0).toUpperCase() }}</div>
                      <div class="d-info">
                        <span class="d-name">{{ c.full_name }}</span>
                        <span class="d-phone">{{ c.phone_number }}</span>
                      </div>
                      <svg v-if="isAlreadyAdded(c.phone_number)" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" class="added-check"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                    </div>
                    <div v-if="searchResults.length === 0 && customerSearch.length > 1" class="dropdown-msg">
                      No contacts found
                    </div>
                  </template>
                </div>
              </div>

              <!-- Divider -->
              <div class="or-divider"><span>or enter a number</span></div>

              <!-- Custom number input -->
              <div class="field-group">
                <label class="field-label">Phone number</label>
                <div class="number-row">
                  <input
                    v-model="customNumber"
                    type="text"
                    placeholder="e.g. 201234567890"
                    class="field-input"
                    @keyup.enter="addCustomNumber"
                    @input="numberError = ''"
                  />
                  <button class="btn-add" @click="addCustomNumber">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
                    Add
                  </button>
                </div>
                <p v-if="numberError" class="field-error">{{ numberError }}</p>
                <p class="field-hint">Digits only, with country code, no + or leading 0</p>
              </div>

              <!-- Bulk paste toggle -->
              <button type="button" class="btn-bulk-toggle" @click="showBulkPaste = !showBulkPaste">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/></svg>
                {{ showBulkPaste ? 'Hide bulk paste' : 'Paste multiple numbers' }}
              </button>

              <!-- Bulk paste panel -->
              <div v-if="showBulkPaste" class="bulk-paste-panel">
                <textarea
                  v-model="bulkPasteText"
                  class="bulk-textarea"
                  placeholder="Paste numbers separated by commas, spaces, or new lines…&#10;201234567890&#10;447911123456&#10;966501234567"
                  rows="4"
                ></textarea>
                <div class="bulk-paste-actions">
                  <span v-if="bulkPasteResult" class="bulk-result">
                    <span class="bulk-added">+{{ bulkPasteResult.added }} added</span>
                    <span v-if="bulkPasteResult.skipped" class="bulk-skipped">{{ bulkPasteResult.skipped }} already in list</span>
                    <span v-if="bulkPasteResult.invalid" class="bulk-invalid">{{ bulkPasteResult.invalid }} invalid</span>
                  </span>
                  <button type="button" class="btn-add" @click="addBulkNumbers">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
                    Add All
                  </button>
                </div>
              </div>

              <!-- Recipient list -->
              <div class="recipients-section">
                <div v-if="recipients.length === 0" class="empty-recipients">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor" class="empty-icon">
                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                  </svg>
                  <p>No recipients yet</p>
                </div>
                <div v-else class="recipient-list">
                  <div
                    v-for="r in recipients"
                    :key="r.wa_id"
                    class="recipient-item-wrap"
                  >
                    <div
                      class="recipient-item"
                      :class="{
                        'status-ok': sendResults[r.wa_id] === 'ok',
                        'status-fail': sendResults[r.wa_id] === 'fail'
                      }"
                    >
                      <div class="r-avatar" :class="{ 'avatar-ok': sendResults[r.wa_id] === 'ok', 'avatar-fail': sendResults[r.wa_id] === 'fail' }">
                        <template v-if="sendResults[r.wa_id] === 'ok'">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                        </template>
                        <template v-else-if="sendResults[r.wa_id] === 'fail'">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                        </template>
                        <template v-else>{{ r.name.charAt(0).toUpperCase() }}</template>
                      </div>
                      <div class="r-info">
                        <span class="r-name">{{ r.name }}</span>
                        <span class="r-phone">{{ r.wa_id }}</span>
                      </div>
                      <span v-if="sendResults[r.wa_id] === 'ok'" class="r-status-label ok">Sent</span>
                      <span v-else-if="sendResults[r.wa_id] === 'fail'" class="r-status-label fail">Failed</span>
                      <template v-else>
                        <!-- Eye: preview variables for this recipient -->
                        <button
                          v-if="templateForm.selectedTemplate && getVariables().length"
                          type="button"
                          class="btn-preview-recipient"
                          :class="{ active: previewingRecipient === r.wa_id }"
                          :title="previewingRecipient === r.wa_id ? 'Hide preview' : 'Preview message variables'"
                          @click="toggleRecipientPreview(r.wa_id)"
                        >
                          <div v-if="previewLoadingFor === r.wa_id" class="spinner xs"></div>
                          <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
                        </button>
                        <button
                          class="btn-remove"
                          :disabled="sending"
                          @click="removeRecipient(r.wa_id)"
                          title="Remove"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                        </button>
                      </template>
                    </div>
                    <!-- Variable preview panel for this recipient -->
                    <div v-if="previewingRecipient === r.wa_id && recipientPreviewData[r.wa_id]" class="recipient-preview-panel">
                      <div v-for="(val, key) in recipientPreviewData[r.wa_id]" :key="key" class="rp-row">
                        <span class="rp-key">{{ key }}</span>
                        <span class="rp-val">{{ val }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ══ RIGHT: Template + Send ══ -->
            <div class="qst-panel right-panel">

              <!-- Loading templates -->
              <div v-if="loadingTemplates" class="panel-loading">
                <div class="spinner lg"></div>
                <p class="state-label">Loading templates…</p>
              </div>

              <template v-else>
                <!-- Template selector -->
                <div class="panel-hd">
                  <div class="panel-hd-left">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" class="panel-hd-icon">
                      <path d="M14,17H7V15H14M17,13H7V11H17M17,9H7V7H17M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3Z"/>
                    </svg>
                    <h2 class="panel-title">Template</h2>
                  </div>
                </div>

                <div class="field-group">
                  <label class="field-label">Select template</label>
                  <select v-model="templateForm.selectedTemplateId" @change="onTemplateSelect" class="field-select">
                    <option value="">Choose an approved template…</option>
                    <option v-for="t in templates" :key="t.id" :value="t.id">{{ t.name }}</option>
                  </select>
                </div>

                <template v-if="templateForm.selectedTemplate">

                  <!-- Media picker -->
                  <div v-if="hasMedia()" class="field-group">
                    <label class="field-label">{{ mediaLabel }} <span class="required-star">*</span></label>

                    <!-- Mode tabs -->
                    <div class="media-tabs">
                      <button type="button" :class="['media-tab', { active: mediaMode === 'upload' }]" @click="setMediaMode('upload')">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z"/></svg>
                        Upload
                      </button>
                      <button type="button" :class="['media-tab', { active: mediaMode === 'existing' }]" @click="setMediaMode('existing')">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z"/></svg>
                        My Media
                      </button>
                      <button type="button" :class="['media-tab', { active: mediaMode === 'url' }]" @click="setMediaMode('url')">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>
                        Direct URL
                      </button>
                    </div>

                    <!-- Upload panel -->
                    <div v-if="mediaMode === 'upload'" class="media-panel">
                      <input type="file" ref="mediaFileInput" class="media-file-hidden" :accept="mediaAcceptTypes" @change="onMediaSelected" />
                      <div v-if="!mediaAssetUuid && !mediaUploadLoading" class="media-upload-area" @click="$refs.mediaFileInput.click()">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" class="upload-icon-svg"><path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z"/></svg>
                        <p class="upload-area-text">Click to select a {{ mediaLabel.toLowerCase() }}</p>
                        <p class="upload-area-hint" v-if="getHeaderComponent().format === 'VIDEO'">MP4 only · Max 16 MB</p>
                        <p class="upload-area-hint" v-else-if="getHeaderComponent().format === 'IMAGE'">JPG or PNG · Max 5 MB</p>
                        <p class="upload-area-hint" v-else>PDF, DOC, DOCX · Max 10 MB</p>
                      </div>
                      <div v-if="mediaUploadLoading" class="media-uploading">
                        <div class="spinner xs"></div>
                        <span>Uploading…</span>
                      </div>
                      <div v-if="mediaAssetUuid && !mediaUploadLoading" class="media-confirmed">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style="color:#22c55e;flex-shrink:0"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                        <span class="media-confirmed-name">{{ mediaAssetName }}</span>
                        <button type="button" class="btn-media-change" @click="clearMedia">Change</button>
                      </div>
                      <p v-if="mediaUploadError" class="field-error">{{ mediaUploadError }}</p>
                    </div>

                    <!-- My Media panel -->
                    <div v-if="mediaMode === 'existing'" class="media-panel">
                      <div v-if="mediaAssetsLoading" class="media-loading-row">
                        <div class="spinner xs"></div> Loading saved media…
                      </div>
                      <div v-else-if="filteredMediaAssets.length === 0" class="media-empty-row">
                        No saved {{ mediaLabel.toLowerCase() }} files yet. Upload one first.
                      </div>
                      <div v-else class="media-assets-grid">
                        <div
                          v-for="asset in filteredMediaAssets"
                          :key="asset.uuid"
                          class="media-asset-card"
                          :class="{ selected: mediaAssetUuid === asset.uuid }"
                          @click="selectMediaAsset(asset)"
                        >
                          <img v-if="asset.media_type === 'image' && asset.preview_url" :src="asset.preview_url" class="asset-thumb" alt="" />
                          <video v-else-if="asset.media_type === 'video' && asset.preview_url" :src="asset.preview_url" class="asset-thumb" preload="metadata" muted />
                          <div v-else class="asset-thumb-fallback">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
                            <span>{{ asset.original_filename }}</span>
                          </div>
                          <div v-if="mediaAssetUuid === asset.uuid" class="asset-selected-badge">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Direct URL panel -->
                    <div v-if="mediaMode === 'url'" class="media-panel">
                      <input
                        v-model="templateForm.mediaUrl"
                        type="url"
                        :placeholder="`https://example.com/file.${getHeaderComponent().format === 'IMAGE' ? 'jpg' : getHeaderComponent().format === 'VIDEO' ? 'mp4' : 'pdf'}`"
                        class="field-input"
                      />
                      <p class="field-hint">Allowed: {{ urlExtensions.map(e => '.' + e).join(', ') }}</p>
                    </div>
                  </div>

                  <!-- Variables -->
                  <div v-if="getVariables().length" class="field-group">
                    <label class="field-label">Template variables</label>
                    <div class="vars-list">
                      <div v-for="v in getVariables()" :key="v" class="var-card">
                        <div class="var-card-top">
                          <code class="var-token">{{ varBadge(v) }}</code>
                          <div class="var-radio-group">
                            <label class="radio-lbl">
                              <input type="radio" :name="`var-${v}`" value="customer_field" v-model="getOrCreateVar(v).source_type" />
                              Customer field
                            </label>
                            <label class="radio-lbl">
                              <input type="radio" :name="`var-${v}`" value="static_value" v-model="getOrCreateVar(v).source_type" />
                              Static value
                            </label>
                          </div>
                        </div>
                        <div v-if="getOrCreateVar(v).source_type === 'customer_field'" class="var-card-inputs">
                          <select v-model="getOrCreateVar(v).customer_field" class="field-select sm">
                            <option value="">Select field…</option>
                            <option value="full_name">Customer Name</option>
                            <option value="phone_number">Phone Number</option>
                            <option value="email">Email</option>
                            <option value="city">City</option>
                            <option value="country">Country</option>
                          </select>
                          <input v-model="getOrCreateVar(v).default_value" type="text" placeholder="Fallback…" class="field-input sm" />
                        </div>
                        <div v-else class="var-card-inputs">
                          <input v-model="getOrCreateVar(v).static_value" type="text" placeholder="Enter value…" class="field-input" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Preview -->
                  <div class="field-group">
                    <label class="field-label">Preview</label>
                    <div class="preview-window">
                      <div class="preview-bar">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="color:#25d366"><path d="M12 1C6.48 1 2 5.48 2 11c0 2.39.86 4.58 2.26 6.31L3 21l3.84-1.22A9.95 9.95 0 0012 21c5.52 0 10-4.48 10-10S17.52 1 12 1z"/></svg>
                        <span>WhatsApp Preview</span>
                      </div>
                      <div class="preview-bg">
                        <div class="bubble">
                          <!-- Header -->
                          <div v-if="getHeaderComponent()" class="bubble-header">
                            <div v-if="getHeaderComponent().format === 'TEXT'" class="bubble-header-text">{{ getHeaderComponent().text }}</div>
                            <div v-else class="bubble-media-placeholder">
                              <template v-if="getHeaderComponent().format === 'IMAGE' && resolvedMediaUrl">
                                <img :src="resolvedMediaUrl" class="bubble-preview-img" />
                              </template>
                              <template v-else-if="getHeaderComponent().format === 'VIDEO' && resolvedMediaUrl">
                                <video :src="resolvedMediaUrl" class="bubble-preview-img" controls muted preload="metadata" />
                              </template>
                              <template v-else>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style="opacity:.4">
                                  <path v-if="getHeaderComponent().format === 'IMAGE'" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                                  <path v-else-if="getHeaderComponent().format === 'VIDEO'" d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
                                  <path v-else d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                                </svg>
                                <span>{{ getHeaderComponent().format === 'IMAGE' ? 'Image' : getHeaderComponent().format === 'VIDEO' ? 'Video' : 'Document' }}</span>
                              </template>
                            </div>
                          </div>
                          <!-- Body -->
                          <div v-if="getBodyComponent()" class="bubble-body" v-html="replaceVars(getBodyComponent().text)"></div>
                          <!-- Footer -->
                          <div v-if="getFooterComponent()" class="bubble-footer">{{ getFooterComponent().text }}</div>
                          <!-- Buttons -->
                          <div v-if="getButtonsComponent()" class="bubble-buttons">
                            <span v-for="(btn, i) in getButtonsComponent().buttons" :key="i" class="bubble-btn">{{ btn.text }}</span>
                          </div>
                          <!-- Timestamp -->
                          <div class="bubble-time">{{ currentTime }}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Send action -->
                  <div class="send-section">
                    <div class="send-summary" v-if="recipients.length">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="color:#25d366"><path d="M13 3L4 14h7v7l9-11h-7V3z"/></svg>
                      Sending to <strong>{{ recipients.length }} recipient{{ recipients.length !== 1 ? 's' : '' }}</strong>
                    </div>
                    <button class="btn-send" :disabled="!canSend || sending" @click="requestSend">
                      <template v-if="sending">
                        <div class="send-progress-wrap">
                          <div class="send-progress-bar" :style="{ width: sendTotal ? (sendSent / sendTotal * 100) + '%' : '0%' }"></div>
                        </div>
                        <span>{{ sendSent }} / {{ sendTotal }}</span>
                      </template>
                      <template v-else>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                        <span>{{ recipients.length > 1 ? `Send to ${recipients.length} Recipients` : 'Send Template' }}</span>
                      </template>
                    </button>
                    <p v-if="!recipients.length" class="send-hint">Add at least one recipient to send</p>
                    <p v-else-if="!templateForm.selectedTemplate" class="send-hint">Select a template to continue</p>
                    <p v-else-if="hasMedia() && !resolvedMediaUrl" class="send-hint">Please provide a {{ mediaLabel.toLowerCase() }}</p>
                  </div>

                </template>

                <!-- No template selected yet -->
                <div v-else class="template-placeholder">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" class="placeholder-icon">
                    <path d="M14,17H7V15H14M17,13H7V11H17M17,9H7V7H17M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3Z"/>
                  </svg>
                  <p>Select a template above to preview it and configure variables</p>
                </div>
              </template>
            </div>

          </div><!-- /qst-body -->
        </div><!-- /qst-container -->

        <!-- Notification Toast -->
        <div v-if="notification" class="notification-toast" :class="notification.type">
          <div class="notification-content">
            <svg v-if="notification.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <div class="notification-text">
              <strong>{{ notification.title }}</strong>
              <p>{{ notification.message }}</p>
            </div>
          </div>
          <button @click="dismissNotification" class="notification-close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

      <!-- ── Confirmation modal ──────────────────────────── -->
      <div v-if="showConfirmModal" class="confirm-overlay" @click.self="showConfirmModal = false">
        <div class="confirm-modal">
          <div class="confirm-icon-wrap">
            <img src="@/assets/whatsapp-logo-wine2.svg" width="32" height="32" alt="" />
          </div>
          <h3 class="confirm-title">Ready to send?</h3>
          <p class="confirm-desc">
            You're about to send <strong>{{ templateForm.selectedTemplate?.name }}</strong> to
            <strong>{{ recipients.length }} recipient{{ recipients.length !== 1 ? 's' : '' }}</strong>.
          </p>
          <div class="confirm-actions">
            <button type="button" class="btn-confirm-cancel" @click="showConfirmModal = false">Cancel</button>
            <button type="button" class="btn-confirm-send" @click="confirmSend">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
              Send Now
            </button>
          </div>
        </div>
      </div>

      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { authStore } from '@/store/auth'
import { DashboardService } from '@/services/dashboard'

const PHONE_REGEX = /^[1-9]\d{6,14}$/

export default {
  name: 'QuickSendTemplateView',
  components: { Sidebar, TopHeader },

  data () {
    const now = new Date()
    const hh = String(now.getHours()).padStart(2, '0')
    const mm = String(now.getMinutes()).padStart(2, '0')
    return {
      // Permission
      checkingPermission: true,
      accessDenied: false,

      // Templates
      loadingTemplates: false,
      templates: [],

      // Template form
      templateForm: {
        selectedTemplateId: '',
        selectedTemplate: null,
        mediaUrl: '',
        variableMappings: []
      },

      // Recipients
      recipients: [],
      sendResults: {},

      // Customer search
      customerSearch: '',
      searchResults: [],
      searchLoading: false,
      showDropdown: false,
      searchTimeout: null,

      // Custom number
      customNumber: '',
      numberError: '',

      // Sending
      sending: false,
      sendProgress: '',

      // Notification
      notification: null,
      notifTimer: null,

      // Preview time label
      currentTime: `${hh}:${mm}`,

      // Media picker
      mediaMode: 'upload',
      mediaAssetUuid: null,
      mediaAssetName: null,
      previewMediaUrl: null,
      mediaUploadLoading: false,
      mediaUploadError: null,
      mediaAssets: [],
      mediaAssetsLoading: false,

      // Bulk paste
      showBulkPaste: false,
      bulkPasteText: '',
      bulkPasteResult: null,

      // Confirmation modal
      showConfirmModal: false,

      // Per-recipient variable preview
      previewingRecipient: null,
      recipientPreviewData: {},
      previewLoadingFor: null,

      // Progress tracking
      sendTotal: 0,
      sendSent: 0
    }
  },

  computed: {
    isAgent () {
      return !!localStorage.getItem('agent_token')
    },
    mediaLabel () {
      const h = this.getHeaderComponent()
      if (!h) return ''
      return h.format.charAt(0) + h.format.slice(1).toLowerCase()
    },
    mediaAcceptTypes () {
      const h = this.getHeaderComponent()
      if (!h) return '*'
      if (h.format === 'VIDEO') return 'video/mp4'
      if (h.format === 'IMAGE') return 'image/jpeg,image/png'
      return 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    },
    urlExtensions () {
      const h = this.getHeaderComponent()
      if (!h) return []
      if (h.format === 'VIDEO') return ['mp4']
      if (h.format === 'IMAGE') return ['jpg', 'jpeg', 'png']
      return ['pdf', 'doc', 'docx']
    },
    filteredMediaAssets () {
      const h = this.getHeaderComponent()
      if (!h) return []
      const type = h.format.toLowerCase()
      return this.mediaAssets.filter(a => a.media_type === type)
    },
    resolvedMediaUrl () {
      if (this.mediaMode === 'url') return this.templateForm.mediaUrl
      return this.previewMediaUrl || ''
    },
    canSend () {
      if (!this.templateForm.selectedTemplate) return false
      if (this.recipients.length === 0) return false
      if (this.hasMedia() && !this.resolvedMediaUrl) return false
      const vars = this.getVariables()
      for (const v of vars) {
        const m = this.templateForm.variableMappings.find(x => x.variable_index === v)
        if (!m) return false
        if (m.source_type === 'static_value' && !m.static_value) return false
        if (m.source_type === 'customer_field' && !m.customer_field) return false
      }
      return true
    }
  },

  async mounted () {
    await this.checkPermission()
    if (!this.accessDenied) {
      await this.loadTemplates()
    }
    document.addEventListener('click', this.onDocumentClick)
  },

  beforeUnmount () {
    document.removeEventListener('click', this.onDocumentClick)
    clearTimeout(this.searchTimeout)
    clearTimeout(this.notifTimer)
  },

  methods: {
    // ── Permission ──────────────────────────────────────
    async checkPermission () {
      this.checkingPermission = true
      if (!this.isAgent) {
        this.checkingPermission = false
        return
      }
      try {
        const token = localStorage.getItem('agent_token')
        const res = await fetch('https://api.quali-d.com/whatsapp/templates/agent-permission', {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
        })
        if (res.ok) {
          const data = await res.json()
          const allowed = data.allow_agent_send_template || false
          authStore.agentTemplatePermission = allowed
          this.accessDenied = !allowed
        } else {
          this.accessDenied = true
        }
      } catch {
        this.accessDenied = true
      } finally {
        this.checkingPermission = false
      }
    },

    // ── Templates ────────────────────────────────────────
    async loadTemplates () {
      this.loadingTemplates = true
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('agent_token')
        const res = await fetch('https://api.quali-d.com/whatsapp/templates?status=APPROVED', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        this.templates = data.success ? (data.templates || []) : []
      } catch (e) {
        console.error('Error loading templates:', e)
      } finally {
        this.loadingTemplates = false
      }
    },

    onTemplateSelect () {
      const t = this.templates.find(x => x.id === this.templateForm.selectedTemplateId)
      this.templateForm.selectedTemplate = t || null
      this.templateForm.mediaUrl = ''
      this.templateForm.variableMappings = []
      // Reset media picker state
      this.mediaMode = 'upload'
      this.mediaAssetUuid = null
      this.mediaAssetName = null
      this.previewMediaUrl = null
      this.mediaUploadError = null
      this.mediaAssets = []
    },

    getHeaderComponent () {
      return this.templateForm.selectedTemplate?.components?.find(c => c.type === 'HEADER') || null
    },
    getBodyComponent () {
      return this.templateForm.selectedTemplate?.components?.find(c => c.type === 'BODY') || null
    },
    getFooterComponent () {
      return this.templateForm.selectedTemplate?.components?.find(c => c.type === 'FOOTER') || null
    },
    getButtonsComponent () {
      return this.templateForm.selectedTemplate?.components?.find(c => c.type === 'BUTTONS') || null
    },
    hasMedia () {
      const h = this.getHeaderComponent()
      return h && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(h.format)
    },
    getVariables () {
      const body = this.getBodyComponent()
      if (!body?.text) return []
      const matches = body.text.match(/\{\{(\d+)\}\}/g)
      if (!matches) return []
      return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '')))]
    },
    varBadge (v) {
      return `\u007b\u007b${v}\u007d\u007d`
    },
    getOrCreateVar (varIndex) {
      let m = this.templateForm.variableMappings.find(x => x.variable_index === varIndex)
      if (!m) {
        m = { variable_index: varIndex, source_type: 'static_value', static_value: '', customer_field: '', default_value: '' }
        this.templateForm.variableMappings.push(m)
      }
      return m
    },
    replaceVars (text) {
      if (!text) return ''
      let result = text
      this.templateForm.variableMappings.forEach(m => {
        const pat = new RegExp(`\\{\\{${m.variable_index}\\}\\}`, 'g')
        const val = m.source_type === 'static_value'
          ? (m.static_value || `{{${m.variable_index}}}`)
          : `[${m.customer_field || m.variable_index}]`
        result = result.replace(pat, `<strong>${val}</strong>`)
      })
      return result.replace(/\n/g, '<br>')
    },

    // ── Customer search ─────────────────────────────────
    debouncedCustomerSearch () {
      clearTimeout(this.searchTimeout)
      if (!this.customerSearch.trim()) {
        this.searchResults = []
        return
      }
      this.searchTimeout = setTimeout(() => this.searchCustomers(), 300)
    },

    async searchCustomers () {
      const q = this.customerSearch.trim()
      if (!q) return
      this.searchLoading = true
      this.searchResults = []
      try {
        const isNumeric = /^\d+$/.test(q)
        const params = isNumeric ? { phone_number: q, per_page: 10 } : { search_name: q, per_page: 10 }
        const result = await DashboardService.getCustomers(params)
        this.searchResults = result.customers || []
      } catch (e) {
        console.error('Customer search error:', e)
      } finally {
        this.searchLoading = false
      }
    },

    onSearchBlur () {
      setTimeout(() => { this.showDropdown = false }, 150)
    },

    onDocumentClick (e) {
      if (this.$refs.searchWrap && !this.$refs.searchWrap.contains(e.target)) {
        this.showDropdown = false
      }
    },

    isAlreadyAdded (phone) {
      return this.recipients.some(r => r.wa_id === phone)
    },

    addRecipientFromCustomer (customer) {
      if (this.isAlreadyAdded(customer.phone_number)) return
      this.recipients.push({ wa_id: customer.phone_number, name: customer.full_name || customer.phone_number })
      this.customerSearch = ''
      this.searchResults = []
      this.showDropdown = false
    },

    // ── Custom number ────────────────────────────────────
    addCustomNumber () {
      const num = this.customNumber.trim()
      if (!num) return
      if (!PHONE_REGEX.test(num)) {
        this.numberError = 'Invalid — digits only, with country code, no + or leading 0 (e.g. 201234567890)'
        return
      }
      if (this.isAlreadyAdded(num)) {
        this.numberError = 'This number is already added'
        return
      }
      this.recipients.push({ wa_id: num, name: num })
      this.customNumber = ''
      this.numberError = ''
    },

    removeRecipient (waId) {
      this.recipients = this.recipients.filter(r => r.wa_id !== waId)
      const updated = { ...this.sendResults }
      delete updated[waId]
      this.sendResults = updated
    },

    // ── Send ─────────────────────────────────────────────
    async fetchCustomerData (phone) {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('agent_token')
        const res = await fetch(`https://api.quali-d.com/customers/all?phone=${phone}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        if (data.success && data.customers?.length) return data.customers[0]
      } catch {}
      return {}
    },

    hasCustomerFields () {
      return this.templateForm.variableMappings.some(m => m.source_type === 'customer_field')
    },

    async sendToAll () {
      if (!this.canSend || this.sending) return
      this.sending = true
      this.sendResults = {}
      const total = this.recipients.length
      this.sendTotal = total
      this.sendSent = 0
      let sent = 0
      const token = localStorage.getItem('token') || localStorage.getItem('agent_token')
      const header = this.getHeaderComponent()

      for (const recipient of this.recipients) {
        sent++
        this.sendSent = sent
        this.sendProgress = `Sending ${sent} / ${total}…`

        let customerData = {}
        if (this.hasCustomerFields()) {
          customerData = await this.fetchCustomerData(recipient.wa_id)
        }

        const bodyParameters = this.templateForm.variableMappings.map(m => {
          if (m.source_type === 'static_value') return m.static_value
          return customerData[m.customer_field] || m.default_value || ''
        })

        const payload = {
          phone_number: recipient.wa_id,
          template_name: this.templateForm.selectedTemplate.name,
          template_language: this.templateForm.selectedTemplate.language,
          body_parameters: bodyParameters
        }

        const mediaUrl = this.resolvedMediaUrl
        if (header?.format === 'IMAGE') payload.image_url = mediaUrl
        else if (header?.format === 'VIDEO') payload.video_url = mediaUrl
        else if (header?.format === 'DOCUMENT') payload.document_url = mediaUrl

        try {
          const res = await fetch('https://api.quali-d.com/whatsapp/templates/send-single', {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          })
          const data = await res.json()
          this.sendResults = { ...this.sendResults, [recipient.wa_id]: data.success ? 'ok' : 'fail' }
        } catch {
          this.sendResults = { ...this.sendResults, [recipient.wa_id]: 'fail' }
        }
      }

      this.sending = false
      this.sendProgress = ''

      const failed = Object.values(this.sendResults).filter(v => v === 'fail').length
      if (failed === 0) {
        this.showNotification({ type: 'success', title: 'Templates Sent', message: `Successfully sent to ${total} recipient${total !== 1 ? 's' : ''}` })
        setTimeout(() => {
          this.templateForm = { selectedTemplateId: '', selectedTemplate: null, mediaUrl: '', variableMappings: [] }
          this.recipients = []
          this.sendResults = {}
          this.recipientPreviewData = {}
          this.previewingRecipient = null
          this.bulkPasteResult = null
        }, 2500)
      } else {
        this.showNotification({ type: 'error', title: 'Partial Failure', message: `${failed} of ${total} messages failed to send` })
      }
    },

    // ── Media picker ──────────────────────────────────────
    setMediaMode (mode) {
      this.mediaMode = mode
      this.mediaAssetUuid = null
      this.mediaAssetName = null
      this.previewMediaUrl = null
      this.mediaUploadError = null
      this.templateForm.mediaUrl = ''
      if (mode === 'existing') this.loadMediaAssets()
    },

    async onMediaSelected (e) {
      const file = e.target.files[0]
      if (!file) return
      this.mediaUploadError = null
      this.mediaUploadLoading = true
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('agent_token')
        const h = this.getHeaderComponent()
        const formData = new FormData()
        formData.append('file', file)
        formData.append('media_type', h ? h.format.toLowerCase() : '')
        const res = await fetch('https://api.quali-d.com/whatsapp/broadcast/media/upload', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
          body: formData
        })
        const data = await res.json()
        if (!res.ok || !data.success) throw new Error(data.message || 'Upload failed')
        this.mediaAssetUuid = data.asset.uuid
        this.mediaAssetName = data.asset.original_filename
        this.previewMediaUrl = data.asset.preview_url || null
      } catch (err) {
        this.mediaUploadError = err.message || 'Upload failed. Please try again.'
      } finally {
        this.mediaUploadLoading = false
        e.target.value = ''
      }
    },

    async loadMediaAssets () {
      this.mediaAssetsLoading = true
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('agent_token')
        const h = this.getHeaderComponent()
        const type = h ? h.format.toLowerCase() : ''
        const res = await fetch(`https://api.quali-d.com/whatsapp/broadcast/media?type=${type}`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
        })
        const data = await res.json()
        this.mediaAssets = data.success ? (data.assets || []) : []
      } catch {
        this.mediaAssets = []
      } finally {
        this.mediaAssetsLoading = false
      }
    },

    selectMediaAsset (asset) {
      this.mediaAssetUuid = asset.uuid
      this.mediaAssetName = asset.original_filename
      this.previewMediaUrl = asset.preview_url || null
    },

    clearMedia () {
      this.mediaAssetUuid = null
      this.mediaAssetName = null
      this.previewMediaUrl = null
      this.mediaUploadError = null
      if (this.$refs.mediaFileInput) this.$refs.mediaFileInput.value = ''
    },

    formatFileSize (bytes) {
      if (!bytes) return ''
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / 1048576).toFixed(1) + ' MB'
    },

    // ── Bulk paste ────────────────────────────────────────
    addBulkNumbers () {
      const raw = this.bulkPasteText.trim()
      if (!raw) return
      const tokens = raw.split(/[\s,;]+/).filter(Boolean)
      let added = 0
      let skipped = 0
      let invalid = 0
      for (const t of tokens) {
        const num = t.replace(/^\+/, '').trim()
        if (!PHONE_REGEX.test(num)) { invalid++; continue }
        if (this.isAlreadyAdded(num)) { skipped++; continue }
        this.recipients.push({ wa_id: num, name: num })
        added++
      }
      this.bulkPasteResult = { added, skipped, invalid }
      if (added > 0) this.bulkPasteText = ''
    },

    // ── Confirmation ──────────────────────────────────────
    requestSend () {
      if (!this.canSend || this.sending) return
      this.showConfirmModal = true
    },
    confirmSend () {
      this.showConfirmModal = false
      this.sendToAll()
    },

    // ── Per-recipient preview ─────────────────────────────
    async toggleRecipientPreview (waId) {
      if (this.previewingRecipient === waId) {
        this.previewingRecipient = null
        return
      }
      this.previewingRecipient = waId
      if (this.recipientPreviewData[waId]) return
      this.previewLoadingFor = waId
      try {
        let customerData = {}
        if (this.hasCustomerFields()) {
          customerData = await this.fetchCustomerData(waId)
        }
        const preview = {}
        this.templateForm.variableMappings.forEach(m => {
          const key = `{{${m.variable_index}}}`
          if (m.source_type === 'static_value') {
            preview[key] = m.static_value || '(empty)'
          } else {
            preview[key] = customerData[m.customer_field] || m.default_value || '(not found)'
          }
        })
        this.recipientPreviewData = { ...this.recipientPreviewData, [waId]: preview }
      } finally {
        this.previewLoadingFor = null
      }
    },

    // ── Notifications ─────────────────────────────────────
    showNotification (payload) {
      clearTimeout(this.notifTimer)
      this.notification = payload
      this.notifTimer = setTimeout(() => { this.notification = null }, 5000)
    },
    dismissNotification () {
      clearTimeout(this.notifTimer)
      this.notification = null
    }
  }
}
</script>

<style scoped>
/* ── Layout ──────────────────────────────────────────── */
.qst-layout {
  display: flex;
  height: 100vh;
  overflow: hidden;
}
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}
.page-content {
  flex: 1;
  overflow-y: auto;
  background: var(--bg-secondary, #f4f6f8);
}

/* ── Full-page states ────────────────────────────────── */
.full-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 14px;
  color: var(--text-tertiary, #9ca3af);
  font-size: 14px;
}
.state-icon-wrap {
  width: 64px; height: 64px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
}
.state-icon-wrap.denied { background: rgba(239,68,68,.1); color: #ef4444; }
.state-title { font-size: 18px; font-weight: 600; color: var(--text-primary); margin: 0; }
.state-desc { font-size: 14px; color: var(--text-tertiary); margin: 0; }
.state-label { color: var(--text-tertiary); font-size: 14px; margin: 0; }

/* ── Container ───────────────────────────────────────── */
.qst-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 28px 28px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* ── Page Header ─────────────────────────────────────── */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.header-icon-wrap {
  width: 44px; height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #25d366, #128c7e);
  display: flex; align-items: center; justify-content: center;
  color: #fff;
  flex-shrink: 0;
}
.page-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 2px;
}
.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #6b7280);
  margin: 0;
}

/* Steps bar */
.steps-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  padding: 10px 16px;
}
.step-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-tertiary, #9ca3af);
  transition: color 0.2s;
}
.step-item.done { color: #25d366; }
.step-num {
  width: 20px; height: 20px;
  border-radius: 50%;
  border: 1.5px solid currentColor;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700;
  flex-shrink: 0;
}
.step-item.done .step-num {
  background: #25d366;
  border-color: #25d366;
  color: #fff;
}
.step-lbl { white-space: nowrap; }
.step-arrow { color: var(--border-secondary); flex-shrink: 0; }

/* ── Body grid ───────────────────────────────────────── */
.qst-body {
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 20px;
  align-items: start;
}
@media (max-width: 860px) {
  .qst-body { grid-template-columns: 1fr; }
}

/* ── Panels ──────────────────────────────────────────── */
.qst-panel {
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-primary);
  border-radius: 14px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
}
.right-panel { min-height: 420px; }

/* Panel header */
.panel-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border-primary);
}
.panel-hd-left { display: flex; align-items: center; gap: 8px; }
.panel-hd-icon { color: var(--text-tertiary, #9ca3af); flex-shrink: 0; }
.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}
.count-chip {
  background: #25d366;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  line-height: 1.5;
}

/* ── Form fields ─────────────────────────────────────── */
.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
}
.field-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.required-star { color: #ef4444; margin-left: 2px; }

.field-input {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid var(--border-secondary);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: var(--bg-primary, #fff);
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.field-input:focus {
  border-color: #25d366;
  box-shadow: 0 0 0 3px rgba(37,211,102,.12);
}
.field-input.sm { padding: 7px 10px; font-size: 13px; }

.field-select {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid var(--border-secondary);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: var(--bg-primary, #fff);
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
  cursor: pointer;
}
.field-select:focus { border-color: #25d366; }
.field-select.sm { padding: 7px 10px; font-size: 13px; flex: 1; }

.field-hint { font-size: 11px; color: var(--text-tertiary, #9ca3af); margin: 0; }
.field-error { font-size: 12px; color: #ef4444; margin: 0; }

/* ── Search box ──────────────────────────────────────── */
.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--border-secondary);
  border-radius: 8px;
  padding: 0 12px;
  background: var(--bg-primary, #fff);
  transition: border-color 0.15s, box-shadow 0.15s;
}
.search-box.focused,
.search-box:focus-within {
  border-color: #25d366;
  box-shadow: 0 0 0 3px rgba(37,211,102,.12);
}
.search-icon-inner { flex-shrink: 0; color: var(--text-tertiary, #9ca3af); }
.search-input-el {
  flex: 1;
  border: none;
  outline: none;
  padding: 9px 0;
  font-size: 14px;
  color: var(--text-primary);
  background: transparent;
}

/* Dropdown */
.search-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0; right: 0;
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,.12);
  z-index: 200;
  max-height: 260px;
  overflow-y: auto;
}
.dropdown-msg {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 13px 16px;
  font-size: 13px;
  color: var(--text-tertiary);
}
.dropdown-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.1s;
}
.dropdown-row:hover { background: var(--bg-secondary, rgba(0,0,0,.04)); }
.dropdown-row.is-added { opacity: 0.55; cursor: default; }
.d-avatar {
  width: 34px; height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #25d366, #128c7e);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 600; flex-shrink: 0;
}
.d-info { flex: 1; min-width: 0; }
.d-name { display: block; font-size: 13px; font-weight: 500; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.d-phone { display: block; font-size: 11px; color: var(--text-tertiary); }
.added-check { color: #25d366; flex-shrink: 0; }

/* ── OR divider ──────────────────────────────────────── */
.or-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-tertiary, #9ca3af);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.or-divider::before,
.or-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--border-primary);
}

/* ── Number input row ────────────────────────────────── */
.number-row { display: flex; gap: 8px; }
.btn-add {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 9px 14px;
  background: #25d366;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  white-space: nowrap;
}
.btn-add:hover { opacity: 0.85; }

/* ── Recipients section ──────────────────────────────── */
.recipients-section {
  flex: 1;
}
.empty-recipients {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 32px 0;
  color: var(--text-tertiary, #9ca3af);
  font-size: 13px;
  text-align: center;
}
.empty-icon { opacity: 0.25; }

.recipient-list { display: flex; flex-direction: column; gap: 6px; }
.recipient-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 9px;
  background: var(--bg-secondary, #f9fafb);
  border: 1px solid transparent;
  transition: border-color 0.15s;
}
.recipient-item.status-ok { border-color: rgba(34,197,94,.3); background: rgba(34,197,94,.06); }
.recipient-item.status-fail { border-color: rgba(239,68,68,.3); background: rgba(239,68,68,.06); }

.r-avatar {
  width: 32px; height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #25d366, #128c7e);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; flex-shrink: 0;
}
.r-avatar.avatar-ok { background: #22c55e; }
.r-avatar.avatar-fail { background: #ef4444; }

.r-info { flex: 1; min-width: 0; }
.r-name { display: block; font-size: 13px; font-weight: 500; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.r-phone { display: block; font-size: 11px; color: var(--text-tertiary); }

.r-status-label {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  flex-shrink: 0;
}
.r-status-label.ok { background: rgba(34,197,94,.15); color: #16a34a; }
.r-status-label.fail { background: rgba(239,68,68,.12); color: #dc2626; }

.btn-remove {
  flex-shrink: 0;
  background: none; border: none;
  color: var(--text-tertiary, #9ca3af);
  cursor: pointer; padding: 3px;
  border-radius: 4px;
  display: flex; align-items: center;
  transition: color 0.12s;
}
.btn-remove:hover { color: #ef4444; }
.btn-remove:disabled { opacity: 0.35; cursor: not-allowed; }

/* ── Panel loading ───────────────────────────────────── */
.panel-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 12px;
  min-height: 200px;
}

/* ── Template placeholder ────────────────────────────── */
.template-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 20px;
  text-align: center;
  color: var(--text-tertiary, #9ca3af);
  font-size: 13px;
  line-height: 1.5;
}
.placeholder-icon { opacity: 0.2; }

/* ── Variables ───────────────────────────────────────── */
.vars-list { display: flex; flex-direction: column; gap: 10px; }
.var-card {
  border: 1px solid var(--border-primary);
  border-radius: 9px;
  padding: 12px 14px;
  display: flex; flex-direction: column; gap: 10px;
  background: var(--bg-secondary, #fafafa);
}
.var-card-top { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.var-token {
  background: rgba(37,211,102,.1);
  color: #128c7e;
  border: 1px solid rgba(37,211,102,.25);
  border-radius: 5px;
  padding: 2px 8px;
  font-size: 12px;
  font-family: monospace;
  flex-shrink: 0;
}
.var-radio-group { display: flex; gap: 14px; flex-wrap: wrap; }
.radio-lbl { display: flex; align-items: center; gap: 5px; font-size: 13px; color: var(--text-secondary); cursor: pointer; }
.var-card-inputs { display: flex; gap: 8px; flex-wrap: wrap; }
.var-card-inputs .field-input,
.var-card-inputs .field-select { min-width: 0; }

/* ── Preview window ──────────────────────────────────── */
.preview-window {
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  overflow: hidden;
}
.preview-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: var(--bg-secondary, #f9fafb);
  border-bottom: 1px solid var(--border-primary);
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.preview-bg {
  padding: 16px;
  background: #e5ddd5;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c8bdb3' fill-opacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}
.bubble {
  background: #fff;
  border-radius: 10px 10px 2px 10px;
  padding: 10px 12px;
  max-width: 340px;
  margin-left: auto;
  box-shadow: 0 1px 3px rgba(0,0,0,.12);
  position: relative;
}
.bubble-header-text {
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 6px;
  color: #111;
}
.bubble-media-placeholder {
  background: rgba(0,0,0,.06);
  border-radius: 7px;
  padding: 20px;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #555;
}
.bubble-preview-img {
  width: 100%;
  max-height: 180px;
  object-fit: cover;
  border-radius: 7px;
  margin-bottom: 8px;
  display: block;
}
.bubble-body {
  font-size: 14px;
  color: #111;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}
.bubble-footer {
  font-size: 12px;
  color: #888;
  margin-top: 6px;
}
.bubble-buttons {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 8px;
  border-top: 1px solid rgba(0,0,0,.08);
  padding-top: 8px;
}
.bubble-btn {
  color: #128c7e;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  padding: 4px 0;
  display: block;
}
.bubble-time {
  font-size: 10px;
  color: #aaa;
  text-align: right;
  margin-top: 4px;
}

/* ── Send section ────────────────────────────────────── */
.send-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.send-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--text-secondary, #6b7280);
  background: rgba(37,211,102,.08);
  border: 1px solid rgba(37,211,102,.2);
  border-radius: 8px;
  padding: 8px 12px;
}
.btn-send {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 13px 20px;
  background: linear-gradient(135deg, #25d366, #128c7e);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;
  box-shadow: 0 2px 8px rgba(37,211,102,.3);
}
.btn-send:not(:disabled):hover { opacity: 0.9; transform: translateY(-1px); }
.btn-send:not(:disabled):active { transform: translateY(0); }
.btn-send:disabled { opacity: 0.5; cursor: not-allowed; box-shadow: none; transform: none; }
.send-hint { font-size: 12px; color: var(--text-tertiary, #9ca3af); margin: 0; text-align: center; }

/* ── Spinners ────────────────────────────────────────── */
.spinner {
  border-radius: 50%;
  border-style: solid;
  border-color: var(--border-primary);
  border-top-color: #25d366;
  animation: spin 0.65s linear infinite;
  flex-shrink: 0;
}
.spinner.lg { width: 32px; height: 32px; border-width: 3px; }
.spinner.xs { width: 14px; height: 14px; border-width: 2px; }
.spinner.white {
  border-color: rgba(255,255,255,.3);
  border-top-color: #fff;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Notification Toast ──────────────────────────────── */
.notification-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  background: var(--bg-primary, #fff);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,.15);
  padding: 14px 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 280px;
  max-width: 380px;
  z-index: 9999;
  animation: slide-in 0.25s ease;
}
.notification-toast.success { border-left: 4px solid #22c55e; }
.notification-toast.success svg { color: #22c55e; }
.notification-toast.error { border-left: 4px solid #ef4444; }
.notification-toast.error svg { color: #ef4444; }
.notification-content { display: flex; align-items: flex-start; gap: 10px; flex: 1; min-width: 0; }
.notification-text { flex: 1; min-width: 0; }
.notification-text strong { display: block; font-size: 13px; color: var(--text-primary); }
.notification-text p { font-size: 12px; color: var(--text-secondary); margin: 2px 0 0; }
.notification-close {
  flex-shrink: 0;
  background: none; border: none;
  color: var(--text-tertiary);
  cursor: pointer; padding: 2px;
  border-radius: 4px;
  display: flex; align-items: center;
  transition: color 0.12s;
}
.notification-close:hover { color: var(--text-primary); }
@keyframes slide-in {
  from { transform: translateY(12px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

/* ── Media picker ────────────────────────────────────── */
.media-tabs {
  display: flex;
  gap: 4px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  padding: 4px;
}
.media-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 6px 10px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  background: transparent;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}
.media-tab:hover { background: var(--bg-primary); color: var(--text-primary); }
.media-tab.active {
  background: var(--bg-primary);
  color: #25d366;
  box-shadow: 0 1px 3px rgba(0,0,0,.08);
}

.media-panel { display: flex; flex-direction: column; gap: 8px; }

/* Upload area */
.media-file-hidden { display: none; }
.media-upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 16px;
  border: 2px dashed var(--border-primary);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  text-align: center;
}
.media-upload-area:hover {
  border-color: #25d366;
  background: rgba(37,211,102,.04);
}
.upload-icon-svg { color: var(--text-tertiary); }
.upload-area-text { font-size: 13px; font-weight: 500; color: var(--text-secondary); margin: 0; }
.upload-area-hint { font-size: 11px; color: var(--text-tertiary); margin: 0; }

.media-uploading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  font-size: 13px;
  color: var(--text-secondary);
  background: var(--bg-secondary);
  border-radius: 8px;
}

.media-confirmed {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(34,197,94,.08);
  border: 1px solid rgba(34,197,94,.25);
  border-radius: 8px;
}
.media-confirmed-name {
  flex: 1;
  font-size: 13px;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.btn-media-change {
  flex-shrink: 0;
  background: none;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 12px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: border-color 0.15s;
}
.btn-media-change:hover { border-color: #25d366; color: #25d366; }

/* My media grid */
.media-loading-row, .media-empty-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  font-size: 13px;
  color: var(--text-tertiary);
}
.media-assets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 8px;
}
.media-asset-card {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid transparent;
  cursor: pointer;
  aspect-ratio: 1;
  background: var(--bg-secondary);
  transition: border-color 0.15s;
}
.media-asset-card:hover { border-color: var(--border-secondary); }
.media-asset-card.selected { border-color: #25d366; }
.asset-thumb {
  width: 100%; height: 100%;
  object-fit: cover;
  display: block;
}
.asset-thumb-fallback {
  width: 100%; height: 100%;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 4px; padding: 8px;
  color: var(--text-tertiary);
  font-size: 10px; text-align: center;
  box-sizing: border-box;
}
.asset-selected-badge {
  position: absolute;
  top: 4px; right: 4px;
  width: 18px; height: 18px;
  border-radius: 50%;
  background: #25d366;
  color: #fff;
  display: flex; align-items: center; justify-content: center;
}

@media (max-width: 600px) {
  .qst-container { padding: 16px; }
  .page-header { flex-direction: column; align-items: flex-start; }
  .steps-bar { width: 100%; }
  .notification-toast { left: 16px; right: 16px; min-width: 0; }
}

/* ── Bulk paste ───────────────────────────────────────── */
.btn-bulk-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: none;
  border: 1px dashed var(--border-secondary);
  border-radius: 7px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-tertiary);
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  align-self: flex-start;
}
.btn-bulk-toggle:hover { border-color: #25d366; color: #25d366; }

.bulk-paste-panel { display: flex; flex-direction: column; gap: 8px; }
.bulk-textarea {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid var(--border-secondary);
  border-radius: 8px;
  font-size: 13px;
  color: var(--text-primary);
  background: var(--bg-primary);
  resize: vertical;
  outline: none;
  font-family: monospace;
  box-sizing: border-box;
  transition: border-color 0.15s;
}
.bulk-textarea:focus { border-color: #25d366; }

.bulk-paste-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.bulk-result { display: flex; align-items: center; gap: 8px; font-size: 12px; flex-wrap: wrap; }
.bulk-added { color: #16a34a; font-weight: 600; }
.bulk-skipped { color: var(--text-tertiary); }
.bulk-invalid { color: #ef4444; }

/* ── Recipient item wrap + eye button ─────────────────── */
.recipient-item-wrap { display: flex; flex-direction: column; }

.btn-preview-recipient {
  flex-shrink: 0;
  background: none;
  border: 1px solid transparent;
  border-radius: 6px;
  color: var(--text-tertiary);
  cursor: pointer;
  padding: 4px 6px;
  display: flex;
  align-items: center;
  transition: color 0.12s, border-color 0.12s;
}
.btn-preview-recipient:hover { color: #128c7e; border-color: var(--border-secondary); }
.btn-preview-recipient.active { color: #25d366; border-color: rgba(37,211,102,.3); background: rgba(37,211,102,.06); }

.recipient-preview-panel {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 10px 12px;
  margin: 3px 0 2px;
  background: rgba(37,211,102,.04);
  border: 1px solid rgba(37,211,102,.2);
  border-radius: 8px;
}
.rp-row { display: flex; align-items: center; gap: 8px; font-size: 12px; flex-wrap: wrap; }
.rp-key {
  font-family: monospace;
  font-size: 11px;
  background: rgba(37,211,102,.1);
  color: #128c7e;
  border: 1px solid rgba(37,211,102,.25);
  border-radius: 4px;
  padding: 1px 6px;
  flex-shrink: 0;
}
.rp-val { color: var(--text-primary); }

/* ── Animated progress bar ────────────────────────────── */
.send-progress-wrap {
  height: 6px;
  width: 100px;
  background: rgba(255,255,255,.25);
  border-radius: 999px;
  overflow: hidden;
  flex-shrink: 0;
}
.send-progress-bar {
  height: 100%;
  background: #fff;
  border-radius: 999px;
  transition: width 0.35s ease;
}

/* ── Confirmation modal ───────────────────────────────── */
.confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 8000;
  backdrop-filter: blur(2px);
}
.confirm-modal {
  background: var(--bg-primary, #fff);
  border-radius: 16px;
  padding: 32px 28px;
  max-width: 380px;
  width: calc(100% - 40px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
  box-shadow: 0 16px 48px rgba(0,0,0,.2);
  animation: pop-in 0.2s ease;
}
@keyframes pop-in {
  from { transform: scale(0.94); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.confirm-icon-wrap {
  width: 56px; height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #25d366, #128c7e);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
}
.confirm-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}
.confirm-desc {
  font-size: 14px;
  color: var(--text-secondary, #6b7280);
  margin: 0;
  line-height: 1.55;
}
.confirm-actions {
  display: flex;
  gap: 10px;
  width: 100%;
  margin-top: 6px;
}
.btn-confirm-cancel {
  flex: 1;
  padding: 11px 16px;
  border: 1px solid var(--border-secondary);
  border-radius: 9px;
  background: transparent;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-confirm-cancel:hover { background: var(--bg-secondary); }
.btn-confirm-send {
  flex: 1;
  padding: 11px 16px;
  border: none;
  border-radius: 9px;
  background: linear-gradient(135deg, #25d366, #128c7e);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: opacity 0.15s;
  box-shadow: 0 2px 8px rgba(37,211,102,.3);
}
.btn-confirm-send:hover { opacity: 0.88; }
</style>
